import type { GraphState } from "@kortyx/core";
import type { GraphSnapshotBundle } from "./graph-snapshot";

export type HumanInputKind = "choice" | "multi-choice" | "text" | "custom";

export interface HumanInputOption {
  id: string;
  label: string;
  description?: string;
  value?: unknown;
}

export interface PendingRequestRecord {
  ready?: boolean;
  responseCompleted?: boolean;
  token: string;
  requestId: string;
  sessionId?: string | undefined;
  /**
   * Framework run identifier used by the checkpointer (thread_id).
   * This must be stable across interrupt/resume, but should not be reused for new messages.
   */
  runId: string;
  /**
   * LangGraph checkpoint id for the paused interrupt within the run thread.
   * Used to resume from the correct interrupt after session rollback.
   */
  graphCheckpointId?: string | undefined;
  graphSnapshot?: GraphSnapshotBundle;
  workflow: string;
  node: string;
  state?: GraphState;
  schema: {
    kind: HumanInputKind;
    multiple: boolean;
    question?: string;
    id?: string;
    schemaId?: string;
    schemaVersion?: string;
    contract?: string;
    request?: unknown;
    meta?: Record<string, unknown>;
  };
  options: HumanInputOption[];
  createdAt: number;
  ttlMs: number;
}

export interface PendingRequestStore {
  list?: () => Promise<PendingRequestRecord[]>;
  take?: (token: string) => Promise<PendingRequestRecord | null>;
  save: (rec: PendingRequestRecord) => Promise<void>;
  get: (token: string) => Promise<PendingRequestRecord | null>;
  delete: (token: string) => Promise<void>;
  update: (
    token: string,
    patch: Partial<PendingRequestRecord>,
  ) => Promise<void>;
}

export function createInMemoryPendingRequestStore(): PendingRequestStore & {
  pruneExpired: (now: number, batchSize: number) => number;
} {
  const store = new Map<string, PendingRequestRecord>();

  const pruneExpired = (t: number, batchSize: number) => {
    let deleted = 0;
    for (const [k, v] of store.entries()) {
      if (deleted === batchSize) break;
      if (t - v.createdAt > v.ttlMs) {
        store.delete(k);
        deleted++;
      }
    }
    return deleted;
  };
  const prune = () => pruneExpired(Date.now(), Number.POSITIVE_INFINITY);

  return {
    pruneExpired,
    async list() {
      prune();
      return JSON.parse(
        JSON.stringify([...store.values()]),
      ) as PendingRequestRecord[];
    },
    async take(token) {
      prune();
      const record = store.get(token);
      store.delete(token);
      return record
        ? (JSON.parse(JSON.stringify(record)) as PendingRequestRecord)
        : null;
    },
    async save(rec) {
      prune();
      store.set(rec.token, rec);
    },
    async get(token) {
      prune();
      return store.get(token) ?? null;
    },
    async delete(token) {
      store.delete(token);
    },
    async update(token, patch) {
      const prev = store.get(token);
      if (!prev) return;
      store.set(token, { ...prev, ...patch });
    },
  };
}
